import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
  Alert,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { fleetApi, fleetApiGo } from '../../../api/axiosClient';
import { useNotifier } from '../../../components/Notifier';
import { useLanguage } from '../../../utils/i18n';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

export default function LicenseDetailEditDialog({
  open,
  id,
  type,
  licenseName,
  targetName,
  isLifetime: propIsLifetime,
  onClose,
  onSuccess
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expDate, setExpDate] = useState(null);
  const [isLifetime, setIsLifetime] = useState(Boolean(propIsLifetime));

  const [media, setMedia] = useState({
    front: null,
    back: null,
    document: null
  });

  const [filesToUpload, setFilesToUpload] = useState({
    front: null,
    back: null,
    document: null
  });

  const [tabIndex, setTabIndex] = useState(0);

  const notify = useNotifier();
  const { t } = useLanguage();

  const isVehicle = type === 'vehicle-license';
  const apiBasePath = isVehicle ? '/vehicle-license-detail' : '/driver-license-detail';

  useEffect(() => {
    if (open && id) {
      setIsLifetime(Boolean(propIsLifetime));
      fetchDetails();
    } else {
      // Reset state
      setExpDate(null);
      setIsLifetime(false);
      setMedia({ front: null, back: null, document: null });
      setFilesToUpload({ front: null, back: null, document: null });
      setTabIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id, propIsLifetime]);

  const fetchDetails = async () => {
    setLoading(true);
    try {
      // Fetch expiration date
      // Sebenarnya exp_date ada di row, tapi lebih aman ambil fresh atau biarkan user melihat date picker kosong jika belum ada
      const joinTable = isVehicle ? 'vehicle_license' : 'driver_license';
      const searchPayload = {
        paging: { start: 0, length: 1 },
        columns: [{ name: 'id', logic_operator: '=', value: String(id), operator: 'AND' }],
        joins: [{ name: joinTable, column_results: ['license_name', 'is_lifetime'] }]
      };

      const res = await fleetApiGo.post(`${apiBasePath}/search${getTzParam()}`, searchPayload);
      const rows = res?.data?.data ?? res?.data ?? [];
      const row = Array.isArray(rows) && rows.length > 0 ? rows[0] : null;

      const checkLifetime = (val) => val === true || val === 1 || val === '1' || val === 'true';
      const detectedLifetime =
        checkLifetime(propIsLifetime) || checkLifetime(row?.join?.[`${joinTable}_is_lifetime`]) || checkLifetime(row?.is_lifetime);

      setIsLifetime(detectedLifetime);

      if (row) {
        if (!detectedLifetime && row.license_exp_date && row.license_exp_date !== '1900-01-01T00:00:00Z') {
          setExpDate(dayjs(row.license_exp_date));
        } else {
          setExpDate(null);
        }
      } else {
        setExpDate(null);
      }

      // Fetch images
      const fetchImage = async (imageType) => {
        try {
          // Some backend uses fleetApi (node) vs fleetApiGo (go) for images. We assume fleetApi based on user url
          const imgRes = await fleetApi.get(`${apiBasePath}/images/${id}${getTzParam()}&imageType=${imageType}`);
          return imgRes?.data?.data ?? imgRes?.data ?? null;
        } catch {
          return null;
        }
      };

      let [frontUrl, backUrl, docUrl] = await Promise.all([fetchImage('front'), fetchImage('back'), fetchImage('document')]);

      // Ensure document is a PDF if returned
      if (docUrl && !docUrl.toLowerCase().includes('.pdf')) {
        docUrl = null;
      } else if (docUrl) {
        try {
          // Fetch the PDF as a Blob to force inline preview and prevent auto-download
          const docResponse = await fetch(docUrl);
          const docBlobRaw = await docResponse.blob();
          const pdfBlob = new Blob([docBlobRaw], { type: 'application/pdf' });
          docUrl = URL.createObjectURL(pdfBlob);
        } catch (e) {
          console.error('Failed to convert PDF to inline blob:', e);
          // Fallback to original url if fetch fails (e.g. CORS)
        }
      }

      setMedia({
        front: frontUrl,
        back: backUrl,
        document: docUrl
      });
      // Keep raw URLs for later cloning when a new ID is generated
    } catch (err) {
      console.error('Failed to fetch detail', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e, imageType) => {
    const file = e.target.files[0];
    if (!file) return;

    setFilesToUpload((prev) => ({ ...prev, [imageType]: file }));

    // Local preview
    const objectUrl = URL.createObjectURL(file);
    setMedia((prev) => ({ ...prev, [imageType]: objectUrl }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let targetId = id;
      // 1. Update status / exp date
      if (expDate && !isLifetime) {
        const payload = {
          license_exp_date: expDate.format('YYYY-MM-DDTHH:mm:ss[Z]')
        };
        // Update endpoint (user requested: PUT apigo /edit-status)
        const res = await fleetApiGo.put(`${apiBasePath}/edit-status/${id}${getTzParam()}`, payload);

        // Extract new ID if the backend generates a new record
        const returnedData = res?.data?.data ?? res?.data;
        if (returnedData && returnedData.id) {
          targetId = returnedData.id;
        } else if (typeof returnedData === 'number' || typeof returnedData === 'string') {
          targetId = returnedData;
        }
      }

      // 2. Upload files
      const uploadPromises = [];
      const types = ['front', 'back', 'document'];
      for (const t of types) {
        let fileToUpload = filesToUpload[t];

        // Jika ID berubah, pastikan tiap tipe media di‑upload.
        // Bila pengguna tidak memberikan file baru, ambil file lama dari server.
        if (String(targetId) !== String(id)) {
          if (!fileToUpload) {
            try {
              // 1. Dapatkan URL gambar lama dari API images
              const endpoint = `${apiBasePath}/images/${id}${getTzParam()}&imageType=${t}`;
              const imgRes = await fleetApi.get(endpoint);
              const rawUrl = imgRes?.data?.data ?? imgRes?.data;

              if (rawUrl && typeof rawUrl === 'string') {
                // 2. Unduh (download) gambar dari rawUrl (S3/Spaces) menjadi Blob
                // CATATAN: Langkah ini bisa terkena error CORS (net::ERR_FAILED) jika diakses dari localhost
                // dan backend (spaces-stg) tidak mengizinkan origin localhost.
                const response = await fetch(rawUrl);
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const blob = await response.blob();
                const ext = t === 'document' ? 'pdf' : 'jpg';
                fileToUpload = new File([blob], `${t}.${ext}`, {
                  type: blob.type || (t === 'document' ? 'application/pdf' : 'image/jpeg')
                });
              }
            } catch (e) {
              console.error(`[Workaround] Gagal mengunduh gambar lama untuk tipe ${t}. Kemungkinan error CORS:`, e);
            }
          }
        }

        if (fileToUpload) {
          const fd = new FormData();
          fd.append('file', fileToUpload);
          const p = fleetApi.post(`${apiBasePath}/uploadfile/${targetId}${getTzParam()}&imageType=${t}`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          uploadPromises.push(p);
        }
      }

      if (uploadPromises.length > 0) {
        await Promise.all(uploadPromises);
      }

      notify({ message: t('license.updateSuccess', 'Update successful'), severity: 'success' });
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Update failed', err);
      notify({ message: t('license.updateFailed', 'Failed to update'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const renderMediaBox = (title, typeKey, accept = 'image/*', infoText = '') => {
    const url = media[typeKey];
    // Karena docUrl di-convert jadi blob (yang tidak memiliki ekstensi .pdf di URL-nya),
    // kita cukup mengecek apakah typeKey-nya 'document' (karena document sekarang di-force hanya PDF)
    const isPdf = typeKey === 'document';

    return (
      <Box
        sx={{
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          p: 2,
          textAlign: 'center',
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
          {title}
        </Typography>

        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 150,
            bgcolor: '#f5f5f5',
            borderRadius: 1,
            mb: 2,
            overflow: 'hidden'
          }}
        >
          {!url ? (
            <Typography variant="body2" color="text.secondary">
              {t('license.noFileAvailable', 'No file available')}
            </Typography>
          ) : isPdf ? (
            <iframe src={url} title={title} width="100%" height="200px" style={{ border: 'none' }} />
          ) : (
            <img src={url} alt={title} style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }} />
          )}
        </Box>

        <Button
          component="label"
          variant="outlined"
          size="small"
          startIcon={<CloudUploadIcon />}
          sx={{ textTransform: 'none', mt: 'auto' }}
        >
          {t('license.uploadNew', 'Upload New')} {title}
          <input type="file" hidden accept={accept} onChange={(e) => handleFileChange(e, typeKey)} />
        </Button>
        {infoText && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            {infoText}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography component="div" sx={{ mb: 2, fontWeight: 'bold', fontSize: '1.25rem' }}>
          {t('license.updateDetails', 'Update License Details')}
        </Typography>
        {licenseName && (
          <Typography variant="subtitle2" color="text.secondary">
            {licenseName} • {targetName}
          </Typography>
        )}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={3}>
              <Grid item size={{ xs: 12, md: 4 }}>
                {isLifetime ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, justifyContent: 'center', height: '100%' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {t('license.expiredDate', 'Expired Date')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Chip label={t('driverLicense.lifetime', 'Lifetime')} color="info" size="medium" sx={{ fontWeight: 600 }} />
                    </Box>
                  </Box>
                ) : (
                  <DatePicker
                    label={t('license.expDate', 'Expiration Date')}
                    format="DD/MM/YYYY"
                    value={expDate}
                    onChange={(newValue) => setExpDate(newValue)}
                    slotProps={{
                      textField: { fullWidth: true, size: 'small' }
                    }}
                  />
                )}
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t(
                    'license.updateAlert',
                    'If you update the data, please make sure to upload / update the latest images and documents of this license.'
                  )}
                </Alert>

                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs value={tabIndex} onChange={handleTabChange} aria-label="Media Tabs">
                    <Tab label={t('license.photo', 'Photo')} />
                    <Tab label={t('license.document', 'Document')} />
                  </Tabs>
                </Box>

                {tabIndex === 0 && (
                  <Grid container spacing={2}>
                    <Grid item size={{ xs: 12, md: 6 }}>
                      {renderMediaBox(t('license.frontPhoto', 'Front Photo'), 'front')}
                    </Grid>
                    <Grid item size={{ xs: 12, md: 6 }}>
                      {renderMediaBox(t('license.backPhoto', 'Back Photo'), 'back')}
                    </Grid>
                  </Grid>
                )}

                {tabIndex === 1 && (
                  <Grid container spacing={2}>
                    <Grid item size={{ xs: 12 }}>
                      {/* Document tab: jika data tidak ada, kita tetap tampilkan box upload agar user bisa upload */}
                      {renderMediaBox(
                        t('license.document', 'Document'),
                        'document',
                        'application/pdf,.pdf',
                        t('license.onlyPdfAllowed', 'Only PDF format is allowed')
                      )}
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </LocalizationProvider>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          {t('buttonTitle.cancel', 'Cancel')}
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={saving || loading}>
          {saving ? t('buttonTitle.saving', 'Saving...') : t('buttonTitle.save', 'Save Updates')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
